import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { clearCoveoTokenAndOrg } from "@NA/Services/ADICoveoService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

interface OwnProps {
    extendedStyles?: ChangeCustomerShipToSelectorStyles;
    setParameter: (parameter: GetShipTosApiParameter) => void;
    parameter: GetShipTosApiParameter;
    searchText: string;
    setSearchText: (searchText: string) => void;
    enableWarehousePickup: boolean;
    fulfillmentMethod: string;
    shipTo?: ShipToModel;
    billToId?: string;
    allowSelectBillTo?: boolean;
    onSelect: (shipTo: ShipToModel) => void;
    onCreateNewAddressClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    isLoading?: boolean;
    handleContinueClicked?: any;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomApplicationState, props: OwnProps) => ({
    shipTosDataView: getShipTosDataView(state, props.parameter),
    billtoChildren: state.ADIGlobalData.ADIBillToStore.billToChildState?.billTos,
    billtoParentProperties: state.ADIGlobalData.ADIBillToStore.billToParentState?.properties,
    billToState: getBillToState(state, state.context.session.billToId).value,
});

export interface ChangeCustomerShipToSelectorStyles {
    shipToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
    title?: TypographyProps;
    shipContainer?: InjectableCss;
    shipTitle?: TypographyProps;
    shipContent?: TypographyProps;
    container?: InjectableCss;
}

export const changeCustomerShipToSelectorStyles: ChangeCustomerShipToSelectorStyles = {
    container: {
        css: css`
            width: 100%;
        `,
    },
    title: {
        css: css`
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            text-transform: capitalize;
            font-weight: 700;
            margin-bottom: 7px;
            display: block;
        `,
    },
    shipContainer: {
        css: css`
            border: 1px solid ${ADIColors.gray13};
            border-bottom: 0;
            padding: 10px;
            cursor: pointer;
            width: 100%;
            :last-child {
                border-bottom: 1px solid ${ADIColors.gray13};
            }

            &.active {
                background-color: ${ADIColors.yellow};
                font-weight: bold;
            }

            :hover {
                background-color: ${ADIColors.gray14};
            }
        `,
    },
    shipTitle: {
        css: css`
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            display: block;
        `,
    },
    shipContent: {
        css: css`
            display: block;
            font-size: 12px;
            margin-left: 38px;
            color: ${ADIColors.gray8};
        `,
    },
};

const ChangeCustomerShipToSelector: FC<Props> = ({
    extendedStyles,
    shipTo,
    billToId,
    shipTosDataView,
    onSelect,
    parameter,
    setParameter,
    searchText,
    setSearchText,
    enableWarehousePickup,
    fulfillmentMethod,
    isLoading,
    billtoChildren,
    handleContinueClicked,
    billtoParentProperties,
    billToState,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerShipToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);

    // This code is to sort the shiptos to be displayed in ascending order according to the P21 ---- P21 Change
    const sortedBillToChildren = Array.isArray(billtoChildren)
        ? [...billtoChildren]?.sort((firstBillTo, secondBillTo) => 
            Number(firstBillTo.customerNumber.split('-')[1]) - Number(secondBillTo.customerNumber.split('-')[1])
        )
        : [];

    const shipToDisplay = sortedBillToChildren?.map(billTo => billTo.shipTos).map(shipto => shipto[0]);

    let dropdownLabel = translate("Select Ship To");
    let dropdownPlaceholder = translate("Search or Select Ship To");

    if (enableWarehousePickup && fulfillmentMethod === FulfillmentMethod.PickUp) {
        dropdownLabel = translate("Recipient Address");
        dropdownPlaceholder = translate("Search or Select Recipient Address");
    }

    useEffect(() => setLoadShipTosParameter(), [billToId]);

    useEffect(() => {
        if (shipTosDataView.value) {
            const options: OptionObject[] = shipTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);
            if (!shipTo && shipTosDataView.value.length === 1) {
                onSelect(shipTosDataView.value[0]);
            }
        }
    }, [shipTosDataView]);

    const selectCustomerHandler = (shipToId?: string, billToId?: string) => {
        if (shipToDisplay?.length && shipToId) {
            const shipTo = shipToDisplay.filter(shipTo => shipTo.id === shipToId)[0];
            const billTo = sortedBillToChildren?.filter(billTo => billTo.id === billToId)[0]; // using sorttedBillToChildren as sorting order ----- P21 Change
            if (shipTo) {
                onSelect(shipTo as ShipToModel);
                handleContinueClicked(shipTo, billTo);
                clearCoveoTokenAndOrg();
            }
        }
    };

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.currentTarget.value);
        setLoadShipTosParameter();
    };

    const setLoadShipTosParameter = () => {
        const apiParameter: GetShipTosApiParameter = {
            ...parameter,
            billToId,
            filter: searchText || undefined,
        };
        setParameter(apiParameter);
    };

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{dropdownLabel}</Typography>
            <StyledWrapper>
                {shipToDisplay?.length && sortedBillToChildren?.length
                    ? shipToDisplay.map((shipTo, index: number) => {
                          return (
                              <StyledWrapper
                                  {...styles.shipContainer}
                                  className={billToState?.id === sortedBillToChildren[index]?.id ? "active" : "inactive"}
                                  onClick={() => selectCustomerHandler(shipTo.id, sortedBillToChildren[index]?.id)}
                              >
                                  <Typography {...styles.shipTitle}>{`${
                                      sortedBillToChildren[index].customerNumber.split("-")[1] || ""
                                  } - ${shipTo.companyName.trim()}`}</Typography>
                                  <Typography {...styles.shipContent}>{shipTo.address1}</Typography>
                                  <Typography {...styles.shipContent}>
                                      {shipTo.city} {shipTo.state?.abbreviation} {shipTo.postalCode}
                                  </Typography>
                              </StyledWrapper>
                          );
                      })
                    : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ChangeCustomerShipToSelector);
